<script>
import Default from '@/layouts/Default.vue';
import CommonSection from '@/components/CommonSection.vue';
import BaseInput from '@/components/BaseInput.vue';
import BaseSelect from '@/components/BaseSelect.vue';
import GuideCategoryList from '@/components/guides/GuideCategoryList.vue';
import GuideFormPopup from '@/components/guides/GuideFormPopup.vue';
import GuideTile from '@/components/guides/GuideTile.vue';
import BaseSwitch from '@/components/BaseSwitch.vue';

import api from '@/api/guide';

export default {
  name: 'GuidesPage',
  components: {
    Default,
    CommonSection,
    BaseInput,
    BaseSelect,
    GuideCategoryList,
    GuideFormPopup,
    GuideTile,
    BaseSwitch,
  },
  data() {
    return {
      selectedCategories: [],
      selectedSort: null,
      searchFilter: '',
      guides: [],
      editMode: false,
    };
  },
  mounted() {
    this.fetch();
  },
  computed: {
    self() { return this.$store.state.auth.user; },

    isAdmin() {
      if (!this.self) return false;

      return this.self.roles.includes('ROLE_ADMIN');
    },

    guideCategories() {
      return this.guides.reduce((acc, value) => [...acc, ...value.categories], []);
    },

    sorts() {
      return {
        author: (a, b) => a.author.localeCompare(b.author),
        guide: (a, b) => a.name.localeCompare(b.name),
        // date: (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
      };
    },

    sortOptions() {
      return [
        { label: 'По автору', value: 'author' },
        { label: 'По названию', value: 'guide' },
        // { label: 'По дате', value: 'date' },
      ];
    },

    guidesWithId() {
      return this.guides.map(guide => {
        const idParts = guide['@id'].split('/');
        const id = idParts[idParts.length - 1];

        return { ...guide, id };
      });
    },

    guidesByAdminMode() {
      return (this.isAdmin && this.editMode) ? this.guidesWithId : this.guidesWithId.filter(guide => guide.video !== null);
    },

    guidesSorted() {
      return this.selectedSort ? [...this.guidesByAdminMode].sort((a, b) => this.sorts[this.selectedSort](a, b)) : this.guidesByAdminMode;
    },

    guidesFiltered() {
      return this.guidesSorted.filter(guide => !this.searchFilter || guide.name.toLowerCase().includes(this.searchFilter.toLowerCase()));
    },

    guidesByCategories() {
      return this.guidesFiltered.filter(guide => {
        const categoriesFiltered = guide.categories.filter(category => this.selectedCategories.includes(category));
        return !this.selectedCategories.length || (categoriesFiltered.length > 0 && categoriesFiltered.length === this.selectedCategories.length);
      });
    },
  },
  methods: {
    async fetch() {
      try {
        const res = await api.fetchList();
        this.guides = res.items;
      } catch (e) {
        this.guides = [];
        this.$push({ text: 'Произошла ошибка при загрузке' });
      }
    },

    async create() {
      const requestData = await this.$refs.form.open(null);

      if (!requestData) return;

      try {
        const res = await api.create(requestData);
        this.guides.push(res);

        this.$push({ text: 'Гайд успешно создан' });
      } catch (e) {
        this.$push({ text: 'Произошла ошибка при создании гайда' });
      }
    },

    async remove(id) {
      const answer = await this.$confirm('Удалить гайд?');

      if (!answer) return;

      try {
        await api.delete(id);
        this.guides = [...this.guidesWithId].filter(guide => guide.id !== id);

        this.$push({ text: 'Гайд был удалён' });
      } catch (e) {
        this.$push({ text: 'Произошла ошибка при удалении' });
      }
    },

    async update(data) {
      const requestData = await this.$refs.form.open(data);

      if (!requestData) return;

      try {
        const newGuide = await api.update(data.id, requestData);
        const guideIndex = this.guidesWithId.findIndex(guide => guide.id === data.id);
        this.guides.splice(guideIndex, 1, newGuide);

        this.$push({ text: 'Гайд обновлён' });
      } catch (e) {
        this.$push({ text: 'Произошла ошибка при обновлении' });
      }
    },
  },
};
</script>

<template lang="pug">
Default
  CommonSection(title="Гайды")
    template(v-slot:button)
      .b-admin-panel(v-if="isAdmin")
        ._switch Режим редактирования
          BaseSwitch(v-model="editMode")
        Btn(v-if="editMode" @click="create" dark xl)
          Icon(name="plus")
          | Новый гай
      Btn(v-else href="/manual2.pdf") Инструкция для смежников
    .b-guides-page
      .b-filters
        BaseInput._search(v-model="searchFilter" iconName="search" label="Поиск гайда")
        BaseSelect._sort(v-model="selectedSort" :options="sortOptions" label="Сортировать по" notSelectedLabel="Не выбрано")
        ._total Всего {{ guidesByAdminMode.length }}
        GuideCategoryList._categories(:selectedCategories="selectedCategories" :guideCategories="guideCategories" @toggle="selectedCategories = $event")
      ._items(v-if="guides.length")
        GuideTile(v-for="guide in guidesByCategories" :key="guide['@id']" :guide="guide")
          ._item-buttons(v-if="isAdmin && editMode")
            Btn(@click="update(guide)") Редактировать
            Btn(@click="remove(guide.id)" grey) Удалить
  GuideFormPopup(ref="form" :guideCategories="guideCategories")
</template>

<style scoped lang="scss">
.guides-page {
  display: flex;
  flex-direction: column;
  gap: 40px;

  &__items {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 36px;
  }

  &__item-buttons {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
  }
}

.filters {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-auto-rows: min-content min-content;
  column-gap: 20px;
  row-gap: 40px;

  &__search {
    grid-row: 1;
    grid-column: 1;
  }

  &__sort {
    grid-row: 1;
    grid-column: 2;
  }

  &__categories {
    grid-row: 2;
    grid-column: 1 / 4 span;
  }

  &__total {
    background: #FDDCC4;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    letter-spacing: 0.01em;
    font-weight: 400;
    font-size: 14px;
    height: 39px;
    box-sizing: border-box;
    padding: 0 15px;
    grid-row: 1;
    grid-column: 4;
    justify-self: end;
  }
}

.admin-panel {
  display: flex;
  gap: 20px;
  align-items: center;

  &__switch {
    display: flex;
    align-items: center;
    gap: 15px;
  }
}

@media (min-width: 768px) and (max-width: 1199px){
  .filters {
    grid-auto-rows: min-content min-content min-content;

    &__search {
      grid-row: 1;
      grid-column: 1 / 4 span;
    }

    &__sort {
      grid-row: 2;
      grid-column: 1;
    }

    &__categories {
      grid-row: 3;
      grid-column: 1 / 4 span;
    }

    &__total {
      grid-row: 2;
      grid-column: 4;
      justify-self: end;
    }
  }

  .guides-page {
    &__items {
      grid-template-columns: repeat(2, 1fr);
      gap: 22px;
    }
  }
}

@media (min-width: 320px) and (max-width: 767px){
  .filters {
    grid-template-columns: 1fr;
    grid-auto-rows: min-content min-content min-content min-content;

    &__search {
      grid-row: 1;
      grid-column: 1;
    }

    &__sort {
      grid-row: 2;
      grid-column: 1;
    }

    &__categories {
      grid-row: 3;
      grid-column: 1;
    }

    &__total {
      grid-row: 4;
      grid-column: 1;
      justify-self: start;
    }
  }

  .guides-page {
    &__items {
      grid-template-columns: 1fr;
      gap: 15px;
    }
  }
}
</style>
